import * as React from "react";
import { useRecordContext } from "react-admin";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { httpClient } from "../api/httpClient";
import { API_BASE_URL } from "../api/config";
import { fmtDateTime as fmtDateTimeFr, fmtMoney as fmtMoneyFr } from "../utils/format";

// RemoteList — affiche dans un onglet de fiche (Pro/Patient/Booking…) une liste
// rattachée à l'enregistrement courant, récupérée sur un endpoint backend
// construit par PATH (ex: /bookings/{id}/PROFESSIONAL, /forfaits/patient/{id}).
// react-admin ReferenceManyField ne convient pas ici car le backend expose ces
// données par segment d'URL et non par query param.
//
// Props :
//  - buildPath(record) => string|null : le chemin relatif à API_BASE_URL
//  - columns: [{ key, label, render?(row) }]
//  - extract?(json) => array : pour les réponses non-array
//  - emptyText
export const RemoteList = ({
  buildPath,
  columns,
  extract,
  emptyText = "Aucune donnée",
}) => {
  const record = useRecordContext();
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);

  const id = record?.id;
  React.useEffect(() => {
    if (!id) return;
    let alive = true;
    const path = buildPath(record);
    if (!path) {
      setRows([]);
      return;
    }
    setRows(null);
    setError(null);
    httpClient(`${API_BASE_URL}${path}`)
      .then(({ json }) => {
        if (!alive) return;
        let data;
        if (extract) data = extract(json);
        else if (Array.isArray(json)) data = json;
        else if (Array.isArray(json?.content)) data = json.content;
        else if (Array.isArray(json?.data)) data = json.data;
        else if (Array.isArray(json?.items)) data = json.items;
        else data = [];
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!alive) return;
        // 404/405 => endpoint pas dispo pour ce user, on traite comme vide.
        if (e?.status === 404 || e?.status === 405) setRows([]);
        else setError(e);
      });
    return () => {
      alive = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error)
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Erreur : {error.message || "chargement impossible"}
      </Typography>
    );
  if (rows == null)
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  if (rows.length === 0)
    return (
      <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );

  return (
    <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.id || i} hover>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align}>
                  {c.render ? c.render(row) : safe(row[c.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const safe = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// RemoteObject — comme RemoteList mais pour un endpoint qui renvoie un OBJET
// unique (ex: /referral/dashboard?userId=… → codes + liste de parrainages).
// `render(data)` reçoit l'objet et retourne le JSX.
export const RemoteObject = ({ buildPath, render, emptyText = "Aucune donnée" }) => {
  const record = useRecordContext();
  const [data, setData] = React.useState(undefined);
  const [error, setError] = React.useState(null);
  const id = record?.id;

  React.useEffect(() => {
    if (!id) return;
    let alive = true;
    const path = buildPath(record);
    if (!path) {
      setData(null);
      return;
    }
    setData(undefined);
    setError(null);
    httpClient(`${API_BASE_URL}${path}`)
      .then(({ json }) => alive && setData(json ?? null))
      .catch((e) => {
        if (!alive) return;
        if (e?.status === 404 || e?.status === 405) setData(null);
        else setError(e);
      });
    return () => {
      alive = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error)
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Erreur : {error.message || "chargement impossible"}
      </Typography>
    );
  if (data === undefined)
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  if (data == null)
    return (
      <Typography sx={{ p: 2 }} color="text.secondary">
        {emptyText}
      </Typography>
    );
  return render(data);
};

// Helpers de rendu réutilisables (format FR : « 31 oct. 2026 14:30 », « 32,00 € »)
export const fmtDate = (ms) => fmtDateTimeFr(ms);

export const fmtMoney = (euros) => fmtMoneyFr(euros);

export default RemoteList;
